import { Link } from "react-router-dom";
import { ArrowRight, Ruler, Shield, Palette, Box, Hexagon, CornerDownRight, AlertTriangle } from "lucide-react";
import { Button } from "../components/ui/button";
import { motion } from "framer-motion";
import { useState } from "react";
import BuildCapacityWidget from "../components/BuildCapacityWidget";

const LandingPage = () => {
  const [showBanner, setShowBanner] = useState(true);
  
  const features = [
    {
      icon: <Ruler className="w-8 h-8" />,
      title: "Custom Dimensions",
      description: "From 2ft to 8ft in any direction. Your reptile, your perfect fit."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Marine Plywood",
      description: "BS1088 certified marine-grade plywood for lasting durability."
    },
    {
      icon: <Palette className="w-8 h-8" />,
      title: "Reptile Safe Stains",
      description: "Premium wood stains in 32 beautiful colours to match your space."
    }
  ];

  const shapes = [
    {
      icon: <Box className="w-12 h-12" />,
      name: "Rectangular",
      description: "Classic design for any space"
    },
    {
      icon: <CornerDownRight className="w-12 h-12" />,
      name: "Corner (L-Shape)",
      description: "Perfect for room corners"
    },
    {
      icon: <Hexagon className="w-12 h-12" />,
      name: "Hexagonal",
      description: "Stunning centrepiece display"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Not Taking Orders Disclaimer Banner */}
      {showBanner && (
        <motion.div
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, type: "spring", stiffness: 120 }}
          className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-[#1a1a2e] via-[#16213e] to-[#1a1a2e] border-b-2 border-[#E07A5F]/60 shadow-lg shadow-[#E07A5F]/10"
          data-testid="pvc-out-of-stock-banner"
        >
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-1 justify-center">
              <AlertTriangle className="w-5 h-5 text-[#E07A5F] shrink-0" />
              <p className="text-sm md:text-base text-[#F4F1DE] font-['Manrope'] text-center">
                <span className="font-bold text-[#E07A5F]">Our PVC stock has slithered off!</span>
                {" "}PVC enclosures are temporarily out of stock — but our Wood, Engineered & Stacking ranges are hatching as normal. New PVC batches coming soon.
              </p>
            </div>
            <button
              onClick={() => setShowBanner(false)}
              className="text-[#F4F1DE]/50 hover:text-[#F4F1DE] transition-colors shrink-0 p-1"
              data-testid="close-banner-btn"
              aria-label="Close banner"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </motion.div>
      )}

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.pexels.com/photos/45246/green-tree-python-python-tree-python-green-45246.jpeg')"
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/60 via-[#0A0A0A]/80 to-[#0A0A0A]" />
        
        <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <img 
              src="/logo.png" 
              alt="ReptiCraft LTD" 
              className="h-32 md:h-40 lg:h-48 w-auto mx-auto drop-shadow-2xl"
            />
          </motion.div>

          <motion.h1 
            className="text-4xl md:text-6xl lg:text-7xl font-bold font-['Space_Grotesk'] tracking-tight mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <span className="text-[#F4F1DE]">BUILD YOUR</span>
            <br />
            <span className="text-gradient">PERFECT HABITAT</span>
          </motion.h1>
          
          <motion.p 
            className="text-lg md:text-xl text-[#F4F1DE]/70 mb-8 max-w-2xl mx-auto font-['Manrope']"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Bespoke reptile enclosures crafted with marine plywood, toughened glass, and premium finishes. 
            Design yours in real-time 3D.
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <Link to="/configurator" data-testid="hero-cta">
              <Button 
                size="lg" 
                className="bg-[#E07A5F] hover:bg-[#D06A4F] text-white rounded-sm font-bold uppercase tracking-widest px-8 py-6 text-lg btn-glow"
              >
                Start Configuration
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>

          <BuildCapacityWidget />
        </div>

        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <div className="w-6 h-10 border-2 border-[#F4F1DE]/30 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-[#E07A5F] rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-4 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto">
          <motion.h2 
            className="text-4xl md:text-5xl font-bold font-['Space_Grotesk'] text-center mb-16 text-[#F4F1DE]"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            Premium Materials
          </motion.h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                className="feature-card bg-[#121212] border border-white/10 p-8 group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="text-[#E07A5F] mb-4 group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold font-['Space_Grotesk'] text-[#F4F1DE] mb-2">
                  {feature.title}
                </h3>
                <p className="text-[#F4F1DE]/60 font-['Manrope']">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Shapes Section */}
      <section className="py-24 px-4 bg-[#0D0D0D]">
        <div className="max-w-6xl mx-auto">
          <motion.h2 
            className="text-4xl md:text-5xl font-bold font-['Space_Grotesk'] text-center mb-4 text-[#F4F1DE]"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            Choose Your Shape
          </motion.h2>
          <motion.p 
            className="text-[#F4F1DE]/60 text-center mb-16 text-lg"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            Three unique designs to fit any space
          </motion.p>
          
          <div className="grid md:grid-cols-3 gap-8">
            {shapes.map((shape, index) => (
              <motion.div
                key={shape.name}
                className="text-center p-8 border border-white/10 bg-[#121212] hover:border-[#E07A5F]/50 transition-all cursor-pointer group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="text-[#E07A5F] mb-4 flex justify-center group-hover:scale-110 transition-transform">
                  {shape.icon}
                </div>
                <h3 className="text-2xl font-bold font-['Space_Grotesk'] text-[#F4F1DE] mb-2">
                  {shape.name}
                </h3>
                <p className="text-[#F4F1DE]/60">
                  {shape.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PVC Enclosures Section */}
      <section className="py-24 px-4 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              className="order-2 md:order-1"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              {/* PVC Enclosure illustration */}
              <div className="relative bg-[#121212] border border-white/10 p-8 rounded-lg">
                <svg viewBox="0 0 300 220" className="w-full max-w-md mx-auto">
                  <defs>
                    <linearGradient id="pvcGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#F5F5F5" />
                      <stop offset="100%" stopColor="#E0E0E0" />
                    </linearGradient>
                  </defs>
                  {/* Back panel */}
                  <polygon points="50,40 250,40 250,180 50,180" fill="url(#pvcGradient)" />
                  {/* Left panel */}
                  <polygon points="50,40 50,180 20,200 20,60" fill="#D0D0D0" />
                  {/* Top panel */}
                  <polygon points="50,40 250,40 220,60 20,60" fill="#E8E8E8" />
                  {/* Front frame */}
                  <polygon points="20,60 220,60 220,200 20,200" fill="url(#pvcGradient)" stroke="#BFBFBF" strokeWidth="1" />
                  {/* Acrylic window */}
                  <rect x="40" y="80" width="160" height="100" fill="rgba(200,220,255,0.3)" stroke="#88ccff" strokeWidth="1" rx="6" />
                  {/* Corner brackets */}
                  <path d="M40,80 L40,95 L45,95 L45,85 L55,85 L55,80 Z" fill="#1a1a1a" />
                  <path d="M200,80 L200,95 L195,95 L195,85 L185,85 L185,80 Z" fill="#1a1a1a" />
                  <path d="M40,180 L40,165 L45,165 L45,175 L55,175 L55,180 Z" fill="#1a1a1a" />
                  <path d="M200,180 L200,165 L195,165 L195,175 L185,175 L185,180 Z" fill="#1a1a1a" />
                  {/* Hinges */}
                  <rect x="22" y="90" width="8" height="20" fill="#1a1a1a" rx="2" />
                  <rect x="22" y="130" width="8" height="20" fill="#1a1a1a" rx="2" />
                  <rect x="22" y="170" width="8" height="20" fill="#1a1a1a" rx="2" />
                  {/* Handle */}
                  <rect x="185" y="125" width="12" height="25" fill="#1a1a1a" rx="3" />
                  {/* Vent strip */}
                  <rect x="40" y="65" width="160" height="8" fill="#2d2d2d" rx="1" />
                </svg>
                <div className="absolute top-4 right-4 bg-blue-500/20 text-blue-400 text-xs px-3 py-1 rounded-full font-medium">
                  NEW
                </div>
              </div>
            </motion.div>
            
            <motion.div
              className="order-1 md:order-2"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-block bg-blue-500/20 text-blue-400 text-sm px-4 py-1 rounded-full font-medium mb-4">
                Now Available
              </div>
              <h2 className="text-4xl md:text-5xl font-bold font-['Space_Grotesk'] text-[#F4F1DE] mb-6">
                PVC<br />
                <span className="text-blue-400">Enclosures</span>
              </h2>
              <p className="text-[#F4F1DE]/60 text-lg mb-6">
                Lightweight, waterproof, and easy to clean. Our PVC enclosures feature clear acrylic 
                hinged doors with premium black hardware - perfect for humid environments and species 
                that require frequent misting.
              </p>
              <ul className="space-y-3 text-[#F4F1DE]/70 mb-8">
                <li className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-blue-400 rounded-full" />
                  100% waterproof PVC panels
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-blue-400 rounded-full" />
                  Clear acrylic doors with rounded corners
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-blue-400 rounded-full" />
                  Stylish black hardware & ventilation
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-blue-400 rounded-full" />
                  6 colour options available
                </li>
              </ul>
              <Link to="/pvc" data-testid="pvc-cta">
                <Button 
                  size="lg"
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm font-bold uppercase tracking-widest px-8 py-4"
                >
                  Design PVC Enclosure
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stacking Section */}
      <section className="py-24 px-4 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-bold font-['Space_Grotesk'] text-[#F4F1DE] mb-6">
                Stacking<br />
                <span className="text-[#E07A5F]">Enclosures</span>
              </h2>
              <p className="text-[#F4F1DE]/60 text-lg mb-6">
                Perfect for breeders and multi-reptile households. Stack 2-5 enclosures 
                vertically with our sturdy metal frame system.
              </p>
              <ul className="space-y-3 text-[#F4F1DE]/70 mb-8">
                <li className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-[#E07A5F] rounded-full" />
                  Sizes from 2ft to 8ft width
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-[#E07A5F] rounded-full" />
                  Stack 2, 3, 4, or 5 units high
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-[#E07A5F] rounded-full" />
                  Bulk discount on labour
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-[#E07A5F] rounded-full" />
                  Included metal support frame
                </li>
              </ul>
              <Link to="/stacking" data-testid="stacking-cta">
                <Button 
                  size="lg"
                  className="bg-[#E07A5F] hover:bg-[#D06A4F] text-white rounded-sm font-bold uppercase tracking-widest px-8 py-4 btn-glow"
                >
                  Configure Stack
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </motion.div>
            
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              {/* Stacking illustration */}
              <svg viewBox="0 0 300 300" className="w-full max-w-md mx-auto">
                <defs>
                  <linearGradient id="stackWood" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#8B5A2B" />
                    <stop offset="100%" stopColor="#6B4423" />
                  </linearGradient>
                </defs>
                {[0, 1, 2, 3].map((i) => (
                  <g key={i} transform={`translate(0, ${(3 - i) * 55})`}>
                    {/* Back */}
                    <polygon points="50,30 250,30 250,80 50,80" fill="url(#stackWood)" opacity="0.9" />
                    {/* Left */}
                    <polygon points="50,30 50,80 80,95 80,45" fill="url(#stackWood)" opacity="0.7" />
                    {/* Right */}
                    <polygon points="250,30 250,80 220,95 220,45" fill="url(#stackWood)" opacity="0.8" />
                    {/* Top */}
                    <polygon points="50,30 250,30 220,45 80,45" fill="url(#stackWood)" opacity="0.6" />
                    {/* Glass front */}
                    <polygon points="80,45 220,45 220,95 80,95" fill="#88ccff" opacity="0.3" stroke="#aaddff" strokeWidth="1" />
                  </g>
                ))}
                {/* Frame lines */}
                <line x1="45" y1="30" x2="45" y2="250" stroke="#666" strokeWidth="3" />
                <line x1="255" y1="30" x2="255" y2="250" stroke="#666" strokeWidth="3" />
              </svg>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Engineered (Budget) Section */}
      <section className="py-24 px-4 bg-[#0E0E0E] border-y border-amber-500/20">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              className="relative order-2 md:order-1"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              {/* Engineered OSB illustration */}
              <svg viewBox="0 0 300 220" className="w-full max-w-md mx-auto">
                <defs>
                  <linearGradient id="osbBoard" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#C69A5B" />
                    <stop offset="100%" stopColor="#8A6A3C" />
                  </linearGradient>
                </defs>
                <polygon points="50,40 250,40 250,160 50,160" fill="url(#osbBoard)" />
                <polygon points="50,40 50,160 80,175 80,55" fill="url(#osbBoard)" opacity="0.75" />
                <polygon points="250,40 250,160 220,175 220,55" fill="url(#osbBoard)" opacity="0.85" />
                <polygon points="50,40 250,40 220,55 80,55" fill="url(#osbBoard)" opacity="0.6" />
                {/* OSB chip pattern */}
                {Array.from({ length: 60 }).map((_, i) => (
                  <rect
                    key={i}
                    x={55 + (i % 10) * 19 + (i % 3) * 3}
                    y={50 + Math.floor(i / 10) * 18 + (i % 4) * 2}
                    width={12 + (i % 5)}
                    height={5 + (i % 3)}
                    fill="#6B4A22"
                    opacity={0.25 + (i % 4) * 0.1}
                    transform={`rotate(${(i * 17) % 45} ${60 + (i % 10) * 19} ${55 + Math.floor(i / 10) * 18})`}
                  />
                ))}
                {/* Glass front */}
                <polygon points="80,55 220,55 220,175 80,175" fill="#88ccff" opacity="0.25" stroke="#aaddff" strokeWidth="1" />
              </svg>
            </motion.div>

            <motion.div
              className="order-1 md:order-2"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-block px-3 py-1 bg-amber-500/20 text-amber-400 text-xs font-semibold rounded mb-4 uppercase tracking-wider">
                Budget-Friendly
              </div>
              <h2 className="text-4xl md:text-5xl font-bold font-['Space_Grotesk'] text-[#F4F1DE] mb-6">
                Engineered<br />
                <span className="text-amber-400">OSB3 Builds</span>
              </h2>
              <p className="text-[#F4F1DE]/60 text-lg mb-6">
                Solid, structural OSB3 engineered board in fixed sizes — the most affordable way to get a
                bespoke-quality viv without the plywood price tag. Perfect for rack setups, quarantine
                tanks and breeder rooms.
              </p>
              <ul className="space-y-3 text-[#F4F1DE]/70 mb-8">
                <li className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-amber-400 rounded-full" />
                  15 fixed sizes (2–6ft W × 2–4ft H × 2ft D)
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-amber-400 rounded-full" />
                  OSB3 @ £30 per 8×4ft sheet — huge savings vs plywood
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-amber-400 rounded-full" />
                  Natural raw-board finish (no stain options)
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-amber-400 rounded-full" />
                  Same toughened glass fronts & sliding doors
                </li>
              </ul>
              <Link to="/engineered" data-testid="engineered-cta">
                <Button
                  size="lg"
                  className="bg-amber-500 hover:bg-amber-600 text-black rounded-sm font-bold uppercase tracking-widest px-8 py-4"
                >
                  Build Budget Enclosure
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="py-32 px-4 relative"
        style={{
          backgroundImage: "url('https://images.pexels.com/photos/275632/pexels-photo-275632.jpeg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed"
        }}
      >
        <div className="absolute inset-0 bg-[#0A0A0A]/90" />
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <motion.h2 
            className="text-4xl md:text-6xl font-bold font-['Space_Grotesk'] text-[#F4F1DE] mb-6"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Ready to Create?
          </motion.h2>
          <motion.p 
            className="text-xl text-[#F4F1DE]/70 mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            Design your perfect enclosure with our real-time 3D configurator. 
            See pricing update instantly as you build.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <Link to="/configurator" data-testid="cta-configurator">
              <Button 
                size="lg"
                className="bg-[#E07A5F] hover:bg-[#D06A4F] text-white rounded-sm font-bold uppercase tracking-widest px-12 py-6 text-lg btn-glow"
              >
                Launch Configurator
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-[#0A0A0A] border-t border-white/10">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center">
          <div className="flex items-center space-x-2 mb-4 md:mb-0">
            <span className="text-xl font-bold font-['Space_Grotesk'] text-[#E07A5F]">REPTI</span>
            <span className="text-xl font-bold font-['Space_Grotesk'] text-[#F4F1DE]">CRAFT</span>
          </div>
          <p className="text-[#F4F1DE]/40 text-sm">
            © 2024 ReptiCraft. Bespoke Reptile Enclosures.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
